import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import { extraSmallTextSize, mediumText } from '../../../../styling/stylingHelper';
import { AccessRequestStatus } from '../../../../stores/appStaffStore';
import FilterDropdown from '../../reports/hub/utilization/FilterDropdown';

interface Restaurant {
  id: string;
  name: string;
}

interface StaffFiltersProps {
  restaurants: Restaurant[];
  statusFilter: AccessRequestStatus | 'all';
  onStatusFilterChange: (status: AccessRequestStatus | 'all') => void;
  /** Empty array means "all restaurants" — the filter is not applied. */
  staffRestaurantFilters: string[];
  onRestaurantFiltersChange: (restaurantIds: string[]) => void;
}

const StaffFilters: React.FC<StaffFiltersProps> = ({
  restaurants,
  statusFilter,
  onStatusFilterChange,
  staffRestaurantFilters,
  onRestaurantFiltersChange,
}) => {
  const { t } = useTranslation();

  const statusOptions = useMemo(
    () =>
      (['all', 'approved', 'pending', 'rejected'] as const).map((status) => ({
        value: status,
        label:
          status === 'all'
            ? t('staff.filter.all')
            : status === 'pending'
              ? t('staff.filter.pending')
              : status === 'approved'
                ? t('staff.filter.approved')
                : t('staff.filter.rejected'),
      })),
    [t]
  );

  const sortedRestaurants = useMemo(
    () => [...restaurants].sort((a, b) => a.name.localeCompare(b.name)),
    [restaurants]
  );

  const restaurantOptions = useMemo(
    () => sortedRestaurants.map((r) => ({ value: r.id, label: r.name })),
    [sortedRestaurants]
  );

  // Driven off the selected ids rather than the restaurant list, so a filter that
  // no longer resolves to a visible restaurant still gets a chip the user can remove
  // instead of silently narrowing the list with no way to undo it.
  const selectedChips = useMemo(
    () =>
      staffRestaurantFilters
        .map((id) => ({ id, name: restaurants.find((r) => r.id === id)?.name || id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [restaurants, staffRestaurantFilters]
  );

  if (restaurants.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <FilterDropdown
          label={t('staff.filter.all')}
          options={statusOptions}
          value={statusFilter ?? 'all'}
          onChange={(value) => onStatusFilterChange(value as AccessRequestStatus | 'all')}
        />
        <FilterDropdown
          label={t('staff.allRestaurantsFilter')}
          options={restaurantOptions}
          value=""
          onChange={() => undefined}
          selectedValues={staffRestaurantFilters}
          onSelectionChange={onRestaurantFiltersChange}
          onSelectAll={() => onRestaurantFiltersChange(sortedRestaurants.map((r) => r.id))}
          onClearAll={() => onRestaurantFiltersChange([])}
          countNoun={t('staff.restaurants')}
          searchable
        />
      </div>

      {selectedChips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {selectedChips.map((chip) => (
            <span
              key={chip.id}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded-full',
                extraSmallTextSize,
                mediumText,
                'bg-darkGreenColor/10 dark:bg-darkGreenColor-dark/15 text-darkGreenColor dark:text-darkGreenColor-dark'
              )}
            >
              {chip.name}
              <button
                type="button"
                onClick={() => onRestaurantFiltersChange(staffRestaurantFilters.filter((id) => id !== chip.id))}
                aria-label={t('staff.removeRestaurantFilter', { name: chip.name })}
                className="rounded-full p-0.5 hover:bg-darkGreenColor/20 dark:hover:bg-darkGreenColor-dark/25"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffFilters;
